import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { kasirNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Package, Search, Plus, Minus, Trash2, ShoppingCart, Printer, Download, Banknote, ArrowLeftRight, QrCode, X, Clock, UserPlus, User, Check, ChevronsUpDown } from 'lucide-react';
import api, { fmtIDR, API } from '@/lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { printReceiptBluetooth } from '@/lib/bluetoothPrint';
import { CustomerFormDialog } from '@/components/customers/CustomersManager';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useWs } from '@/lib/WsContext';

export default function KasirPOS() {
  const [products, setProducts] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState([]); // [{product, quantity}]
  const [salesmanId, setSalesmanId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [lastTx, setLastTx] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Tempo details state
  const [tempo, setTempo] = useState({
    staff_pj: '',
    project_name: '',
    has_dp: false,
    dp_type: 'nominal', // 'nominal' | 'percentage'
    dp_value: '',
    location_kabupaten: '',
    location_desa: '',
    location_pj: '',
    notes: '',
  });
  const [dueDate, setDueDate] = useState('');

  const load = async () => {
    const [p, s, cus] = await Promise.all([
      api.get('/products?active_only=true'),
      api.get('/users?role=salesman'),
      api.get('/customers'),
    ]);
    setProducts(p.data);
    setSalesmen(s.data);
    setCustomers(cus.data);
  };
  useEffect(() => { load(); }, []);

  // Subscribe to WebSocket for real-time product changes (add/delete)
  const { subscribe } = useWs();
  useEffect(() => {
    const unsub = subscribe((msg) => {
      if (msg.event === 'product_changed') {
        const action = msg.data?.action;
        const name = msg.data?.name || 'Barang';
        if (action === 'created') {
          toast.success(`Barang baru tersedia: ${name}`, { duration: 3500 });
        } else if (action === 'deleted') {
          toast.info(`Barang dihapus: ${name}`, { duration: 3500 });
          // Also remove from any active cart entries
          setCart((prev) => prev.filter((c) => c.product.id !== msg.data?.product_id));
        }
        // Refresh product list so POS reflects current catalog
        api.get('/products?active_only=true').then(({ data }) => setProducts(data)).catch(() => {});
      } else if (msg.event === 'transaction_deleted') {
        // Stock may have been restored elsewhere — refresh to sync
        api.get('/products?active_only=true').then(({ data }) => setProducts(data)).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
  }, [products, query]);

  const addToCart = (p) => {
    if (p.stock <= 0) return toast.error('Stok habis');
    setCart((prev) => {
      const exist = prev.find((c) => c.product.id === p.id);
      if (exist) {
        if (exist.quantity + 1 > p.stock) { toast.error('Stok tidak cukup'); return prev; }
        return prev.map((c) => c.product.id === p.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { product: p, quantity: 1, discountPct: null, customPrice: null, includePpn: false }];
    });
  };

  const updateDiscountPct = (pid, value) => {
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      if (value === '' || value === null || value === undefined) return { ...c, discountPct: null, customPrice: null };
      const v = Number(value);
      if (isNaN(v) || v < 0) return c;
      if (v > 100) { toast.error('Discount % maksimal 100'); return { ...c, discountPct: 100, customPrice: null }; }
      return { ...c, discountPct: v, customPrice: null };
    }));
  };

  const updateCustomPrice = (pid, value) => {
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      if (value === '' || value === null || value === undefined) return { ...c, customPrice: null };
      const v = Number(value);
      if (isNaN(v) || v < 0) return c;
      return { ...c, customPrice: v, discountPct: null };
    }));
  };

  const togglePpn = (pid) => {
    setCart((prev) => prev.map((c) => c.product.id !== pid ? c : { ...c, includePpn: !c.includePpn }));
  };

  const updateQty = (pid, delta) => {
    setCart((prev) => prev.flatMap((c) => {
      if (c.product.id !== pid) return [c];
      const q = c.quantity + delta;
      if (q <= 0) return [];
      if (q > c.product.stock) { toast.error('Stok tidak cukup'); return [c]; }
      return [{ ...c, quantity: q }];
    }));
  };
  const setQty = (pid, value) => {
    // Allow empty string during typing (kept as 1 minimum on blur)
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      if (value === '' || value === null || value === undefined) {
        return { ...c, quantity: '' };
      }
      const n = Math.floor(Number(value));
      if (isNaN(n) || n < 0) return c;
      if (n === 0) return { ...c, quantity: 0 };
      if (n > c.product.stock) {
        toast.error(`Stok tidak cukup (maks ${c.product.stock})`);
        return { ...c, quantity: c.product.stock };
      }
      return { ...c, quantity: n };
    }));
  };
  const commitQty = (pid) => {
    // On blur: coerce empty/0 back to 1
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      const q = Number(c.quantity);
      if (!q || q < 1 || isNaN(q)) return { ...c, quantity: 1 };
      return c;
    }));
  };
  const removeItem = (pid) => setCart((p) => p.filter((c) => c.product.id !== pid));

  // Kirim sekarang (partial first shipment - untuk kapasitas truk).
  // null/empty = kirim penuh; angka <= quantity = split. Backend akan validasi ulang.
  const updateShipNow = (pid, value) => {
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      if (value === '' || value === null || value === undefined) {
        return { ...c, shipNow: null };
      }
      const n = Math.floor(Number(value));
      if (isNaN(n) || n < 0) return c;
      const q = Number(c.quantity) || 0;
      // Batasi di [0, quantity]. Kalau input > qty, set = qty (tidak boleh over).
      return { ...c, shipNow: Math.min(n, q) };
    }));
  };

  const PPN_RATE = 0.11;

  const basePrice = (c) => {
    // Base price (sebelum PPN)
    if (c.customPrice != null && c.customPrice !== '') {
      const cp = Number(c.customPrice);
      return isNaN(cp) || cp < 0 ? 0 : cp;
    }
    const pct = c.discountPct != null ? Number(c.discountPct) : 0;
    return Math.max(0, c.product.selling_price * (1 - pct / 100));
  };

  const effectiveUnitPrice = (c) => {
    const base = basePrice(c);
    return c.includePpn ? base * (1 + PPN_RATE) : base;
  };

  const total = cart.reduce((a, c) => a + effectiveUnitPrice(c) * (Number(c.quantity) || 0), 0);

  const checkout = async () => {
    if (cart.length === 0) return toast.error('Cart kosong');
    // Ensure all quantities are valid integers >= 1 (coerce any empty string during typing)
    const invalid = cart.find((c) => !Number(c.quantity) || Number(c.quantity) < 1);
    if (invalid) return toast.error(`Kuantitas barang "${invalid.product.name}" tidak valid`);
    if (!customerId) return toast.error('Pilih atau daftarkan customer terlebih dahulu');
    if (!salesmanId) return toast.error('Pilih salesman dulu');
    if ((paymentMethod === 'transfer' || paymentMethod === 'qris') && !paymentRef) return toast.error('Isi referensi pembayaran');
    let tempoDetails = null;
    if (paymentMethod === 'tempo') {
      if (!tempo.staff_pj || !tempo.project_name) return toast.error('Isi Staff Penanggung Jawab dan Nama Proyek');
      if (tempo.has_dp) {
        const v = Number(tempo.dp_value);
        if (isNaN(v) || v < 0) return toast.error('Nilai DP tidak valid');
        if (tempo.dp_type === 'percentage' && v > 100) return toast.error('Persentase DP maksimal 100%');
      }
      tempoDetails = {
        scheme: 'proyek_termin',
        staff_pj: tempo.staff_pj,
        project_name: tempo.project_name,
        has_dp: tempo.has_dp,
        dp_type: tempo.has_dp ? tempo.dp_type : null,
        dp_value: tempo.has_dp ? Number(tempo.dp_value) : null,
        location_kabupaten: tempo.location_kabupaten || null,
        location_desa: tempo.location_desa || null,
        location_pj: tempo.location_pj || null,
        notes: tempo.notes || null,
      };
    }
    try {
      // Build initial_shipment payload jika ada item yang tidak kirim penuh
      // (kirim sekarang < quantity). Diserahkan ke backend sebagai partial batch#1.
      const initialShipment = cart
        .filter((c) => c.shipNow != null && c.shipNow !== '' && Number(c.shipNow) !== Number(c.quantity))
        .map((c) => ({
          product_id: c.product.id,
          quantity: Math.max(0, Math.min(Number(c.shipNow) || 0, Number(c.quantity) || 0)),
        }));
      const { data } = await api.post('/transactions/checkout', {
        items: cart.map((c) => ({
          product_id: c.product.id,
          quantity: c.quantity,
          discount_percentage: c.customPrice == null && c.discountPct != null ? Number(c.discountPct) : null,
          custom_unit_price: c.customPrice != null && c.customPrice !== '' ? Number(c.customPrice) : null,
          include_ppn: !!c.includePpn,
        })),
        salesman_id: salesmanId,
        customer_id: customerId,
        payment_method: paymentMethod,
        payment_reference: paymentRef || null,
        tempo_details: tempoDetails,
        due_date: paymentMethod === 'tempo' && dueDate ? dueDate : null,
        initial_shipment: initialShipment.length > 0 ? initialShipment : null,
      });
      toast.success(`Transaksi ${data.invoice_no} berhasil`);
      setLastTx(data);
      setCart([]);
      setPaymentRef('');
      setCustomerId('');
      setDueDate('');
      setTempo({ staff_pj: '', project_name: '', has_dp: false, dp_type: 'nominal', dp_value: '', location_kabupaten: '', location_desa: '', location_pj: '', notes: '' });
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal checkout');
    }
  };

  const downloadPDF = async (tx) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/transactions/${tx.id}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `receipt-${tx.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Gagal unduh PDF'); }
  };

  const printBT = async (tx) => {
    try { await printReceiptBluetooth(tx); toast.success('Struk terkirim ke printer'); }
    catch (e) { toast.error(e.message || 'Bluetooth gagal (browser tidak mendukung / batal)'); }
  };

  const Cart = (
    <CartPanel
      cart={cart} total={total}
      salesmen={salesmen} salesmanId={salesmanId} setSalesmanId={setSalesmanId}
      customers={customers}
      customerId={customerId} setCustomerId={setCustomerId}
      onAddCustomer={() => setCustomerDialogOpen(true)}
      paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
      paymentRef={paymentRef} setPaymentRef={setPaymentRef}
      tempo={tempo} setTempo={setTempo}
      dueDate={dueDate} setDueDate={setDueDate}
      updateQty={updateQty} setQty={setQty} commitQty={commitQty} removeItem={removeItem}
      updateDiscountPct={updateDiscountPct}
      updateCustomPrice={updateCustomPrice}
      updateShipNow={updateShipNow}
      togglePpn={togglePpn}
      onCheckout={checkout}
      lastTx={lastTx} onDownloadPDF={downloadPDF} onPrintBT={printBT}
    />
  );

  return (
    <AppShell nav={kasirNav} title="POS Kasir">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Cari barang (nama/SKU/kategori)" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 h-11" data-testid="pos-search" />
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="pos-product-grid">
            {filtered.map((p) => {
              const low = p.stock > 0 && p.stock <= (p.low_stock_threshold || 5);
              const out = p.stock <= 0;
              return (
                <Card key={p.id} className={`overflow-hidden hover:shadow-md transition-shadow ${out ? 'opacity-60' : ''}`} data-testid={`pos-product-card-${p.id}`}>
                  <button disabled={out} onClick={() => addToCart(p)} className="w-full text-left disabled:cursor-not-allowed" data-testid={`pos-add-${p.id}`}>
                    <div className="aspect-square bg-muted overflow-hidden">
                      {p.photo_url ? (
                        <img src={p.photo_url} alt={p.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center"><Package className="h-10 w-10 text-muted-foreground" /></div>
                      )}
                    </div>
                    <div className="p-3 space-y-1">
                      <div className="text-[10px] font-mono text-muted-foreground truncate">{p.sku}</div>
                      <div className="font-medium text-sm line-clamp-2 min-h-[40px]">{p.name}</div>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                        <div className={`text-xs ${low ? 'text-rose-700 font-semibold' : out ? 'text-rose-700 font-bold' : 'text-emerald-700'}`}>
                          {out ? 'Habis' : `Stok ${p.stock}`}
                        </div>
                      </div>
                      <div className="font-heading font-semibold tabular-nums pt-1">{fmtIDR(p.selling_price)}</div>
                    </div>
                  </button>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-4">
          <div className="sticky top-20">{Cart}</div>
        </div>
      </div>

      {/* Mobile cart drawer */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button className="h-14 w-14 rounded-full shadow-lg" data-testid="pos-mobile-cart-button">
              <ShoppingCart className="h-5 w-5" />
              {cart.length > 0 && <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">{cart.length}</span>}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto">
            <div className="p-4">{Cart}</div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pos-add-customer-dialog">
          <DialogHeader>
            <DialogTitle>Daftarkan Customer Baru</DialogTitle>
          </DialogHeader>
          <CustomerFormDialog
            inline
            onSaved={async (created) => {
              // Reload customers list and auto-select the new one
              const { data } = await api.get('/customers');
              setCustomers(data);
              if (created?.id) setCustomerId(created.id);
              setCustomerDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function CartPanel(props) {
  const { cart, total, salesmen, salesmanId, setSalesmanId, customers, customerId, setCustomerId, onAddCustomer, paymentMethod, setPaymentMethod, paymentRef, setPaymentRef, tempo, setTempo, dueDate, setDueDate, updateQty, setQty, commitQty, removeItem, updateDiscountPct, updateCustomPrice, updateShipNow, togglePpn, onCheckout, lastTx, onDownloadPDF, onPrintBT } = props;
  const PPN_RATE = 0.11;
  const cartTotalPpn = cart.reduce((a, c) => {
    if (!c.includePpn) return a;
    const base = c.customPrice != null && c.customPrice !== '' ? Number(c.customPrice) : c.product.selling_price * (1 - (Number(c.discountPct) || 0) / 100);
    return a + Math.max(0, base) * PPN_RATE * (Number(c.quantity) || 0);
  }, 0);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const dpAmount = tempo?.has_dp
    ? (tempo.dp_type === 'percentage' ? total * (Number(tempo.dp_value) || 0) / 100 : Number(tempo.dp_value) || 0)
    : 0;
  const remaining = Math.max(0, total - dpAmount);

  return (
    <Card className="border shadow-sm">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <div className="font-heading font-semibold">Keranjang ({cart.length})</div>
        </div>

        {cart.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">Belum ada barang di keranjang. Klik produk untuk menambahkan.</div>
        ) : (
          <div
            className="max-h-[45vh] sm:max-h-96 overflow-y-auto overscroll-contain pr-1 -mr-1"
            data-testid="pos-cart-scroll"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <ul className="space-y-2" data-testid="pos-cart-items">
              <AnimatePresence>
                {cart.map((c) => {
                  const catalog = c.product.selling_price;
                  const hasCustomPrice = c.customPrice != null && c.customPrice !== '';
                  const customPriceNum = hasCustomPrice ? Number(c.customPrice) : null;
                  const pctFromInput = c.discountPct != null ? Number(c.discountPct) : 0;
                  // Base price (sebelum PPN)
                  let basePrice = catalog;
                  if (hasCustomPrice && !isNaN(customPriceNum) && customPriceNum >= 0) {
                    basePrice = customPriceNum;
                  } else {
                    basePrice = Math.max(0, catalog * (1 - pctFromInput / 100));
                  }
                  const ppnAmount = c.includePpn ? basePrice * PPN_RATE : 0;
                  const finalPrice = basePrice + ppnAmount;
                  // Derived discount info (from base vs catalog, not affected by PPN)
                  const discAmount = catalog - basePrice;
                  const pct = catalog > 0 ? Math.max(0, (discAmount / catalog) * 100) : 0;
                  const hasDisc = discAmount > 0.005;
                  const isMarkup = discAmount < -0.005;
                  const qtySafe = Number(c.quantity) || 0;
                  const subtotal = finalPrice * qtySafe;
                  const discPctDisplay = hasCustomPrice
                    ? (hasDisc ? Number(pct.toFixed(pct % 1 === 0 ? 0 : 1)) : (isMarkup ? '' : 0))
                    : (c.discountPct == null ? '' : c.discountPct);
                  return (
                    <motion.li key={c.product.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-2 rounded-lg border space-y-2" data-testid={`cart-item-${c.product.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.product.name}</div>
                          <div className="text-[10px] text-muted-foreground tabular-nums flex flex-wrap gap-1 items-center">
                            Katalog: {fmtIDR(catalog)}
                            {hasDisc && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[9px] py-0 px-1">-{pct.toFixed(pct % 1 === 0 ? 0 : 1)}%</Badge>}
                            {isMarkup && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[9px] py-0 px-1">+{fmtIDR(-discAmount)}</Badge>}
                            {c.includePpn && <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[9px] py-0 px-1">+ PPN 11%</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQty(c.product.id, -1)} data-testid={`pos-qty-decrement-${c.product.id}`}><Minus className="h-3 w-3" /></Button>
                          <Input
                            type="number"
                            min="1"
                            max={c.product.stock}
                            step="1"
                            inputMode="numeric"
                            value={c.quantity === '' ? '' : c.quantity}
                            onChange={(e) => setQty(c.product.id, e.target.value)}
                            onBlur={() => commitQty(c.product.id)}
                            onFocus={(e) => e.target.select()}
                            className="h-6 w-14 px-1 text-center text-xs tabular-nums font-semibold"
                            data-testid={`pos-qty-input-${c.product.id}`}
                          />
                          <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQty(c.product.id, +1)} data-testid={`pos-qty-increment-${c.product.id}`}><Plus className="h-3 w-3" /></Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600" onClick={() => removeItem(c.product.id)} data-testid={`pos-remove-${c.product.id}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>

                      {/* Row 1: Manual Price (Rp) */}
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] text-muted-foreground shrink-0 w-20">Harga Manual:</div>
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">Rp</span>
                          <Input
                            type="number" min="0" step="1" inputMode="numeric"
                            placeholder={`${catalog.toLocaleString('id-ID')} (katalog)`}
                            value={c.customPrice == null ? '' : c.customPrice}
                            onChange={(e) => updateCustomPrice(c.product.id, e.target.value)}
                            className="h-7 text-xs tabular-nums pl-7"
                            data-testid={`pos-custom-price-${c.product.id}`}
                          />
                        </div>
                        <div className="text-xs tabular-nums font-semibold shrink-0 min-w-[80px] text-right" data-testid={`pos-subtotal-${c.product.id}`}>{fmtIDR(subtotal)}</div>
                      </div>

                      {/* Row 2: Discount % */}
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] text-muted-foreground shrink-0 w-20">Discount %:</div>
                        <div className="relative flex-1">
                          <Input
                            type="number" min="0" max="100" step="0.5"
                            placeholder="0"
                            value={discPctDisplay}
                            onChange={(e) => updateDiscountPct(c.product.id, e.target.value)}
                            className="h-7 text-xs tabular-nums pr-6"
                            data-testid={`pos-discount-pct-${c.product.id}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">%</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground shrink-0 min-w-[80px] text-right tabular-nums" data-testid={`pos-final-price-${c.product.id}`}>
                          @{fmtIDR(finalPrice)}
                        </div>
                      </div>

                      {/* Row 3: PPN toggle */}
                      <label className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-xs transition-colors ${c.includePpn ? 'bg-violet-50 border-violet-300 text-violet-800' : 'border-border/60 hover:bg-accent/40'}`}>
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!c.includePpn}
                            onChange={() => togglePpn(c.product.id)}
                            className="rounded"
                            data-testid={`pos-ppn-toggle-${c.product.id}`}
                          />
                          <span className="font-medium">Include PPN 11%</span>
                        </span>
                        {c.includePpn && (
                          <span className="text-[10px] tabular-nums">
                            + {fmtIDR(ppnAmount)}/unit • total PPN: <b>{fmtIDR(ppnAmount * qtySafe)}</b>
                          </span>
                        )}
                      </label>

                      {/* Row 4: Kirim Sekarang — untuk split kapasitas truk. Hanya tampil bila qty ≥ 2 */}
                      {qtySafe >= 2 && (() => {
                        const shipNow = c.shipNow == null || c.shipNow === '' ? qtySafe : Number(c.shipNow);
                        const clamped = Math.max(0, Math.min(shipNow, qtySafe));
                        const remain = qtySafe - clamped;
                        const isPartial = c.shipNow != null && c.shipNow !== '' && Number(c.shipNow) < qtySafe;
                        return (
                          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs transition-colors ${isPartial ? 'bg-amber-50 border-amber-300' : 'border-border/60'}`}>
                            <div className="text-[10px] text-muted-foreground shrink-0 w-20">Kirim sekarang:</div>
                            <Input
                              type="number" min="0" max={qtySafe} step="1" inputMode="numeric"
                              placeholder={`${qtySafe} (kirim penuh)`}
                              value={c.shipNow == null ? '' : c.shipNow}
                              onChange={(e) => updateShipNow(c.product.id, e.target.value)}
                              className="h-7 w-20 px-2 text-xs tabular-nums"
                              data-testid={`pos-ship-now-${c.product.id}`}
                              title="Isi jumlah yang dikirim di batch pertama (Surat Jalan #1). Sisanya bisa dibuatkan Surat Jalan tambahan nanti."
                            />
                            <div className="flex-1 text-[10px] tabular-nums text-right">
                              {isPartial ? (
                                <span className="text-amber-800">
                                  🚚 Kirim <b>{clamped}</b> btg • <b className="text-rose-700">Sisa {remain}</b> (Surat Jalan lanjutan)
                                </span>
                              ) : (
                                <span className="text-muted-foreground">Kosongkan atau samakan qty = kirim penuh</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {(hasDisc || isMarkup) && (
                        <div className={`text-[10px] tabular-nums rounded px-2 py-1 ${hasDisc ? 'bg-emerald-50 text-emerald-800 border border-emerald-100' : 'bg-sky-50 text-sky-800 border border-sky-100'}`}>
                          {hasDisc ? (
                            <>
                              💰 Diskon <b>{pct.toFixed(pct % 1 === 0 ? 0 : 1)}%</b> — hemat <b>{fmtIDR(discAmount)}</b>/unit
                              {hasCustomPrice && <span className="ml-1 text-emerald-600">(dari harga manual)</span>}
                            </>
                          ) : (
                            <>📈 Markup: harga <b>{fmtIDR(-discAmount)}</b>/unit lebih tinggi dari katalog</>
                          )}
                        </div>
                      )}
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </div>
        )}

        <Separator />

        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1">Customer <span className="text-rose-600">*</span></Label>
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={onAddCustomer} data-testid="pos-add-customer-button">
                <UserPlus className="h-3 w-3 mr-1" /> Baru
              </Button>
            </div>
            <CustomerCombobox
              customers={customers}
              value={customerId}
              onChange={setCustomerId}
            />
            {selectedCustomer && (
              <div className="text-xs text-muted-foreground bg-accent/40 rounded p-2 space-y-0.5" data-testid="pos-selected-customer-info">
                {selectedCustomer.company && <div>🏢 {selectedCustomer.company}</div>}
                {selectedCustomer.whatsapp && <div>📱 {selectedCustomer.whatsapp}</div>}
                {selectedCustomer.location && <div>📍 {selectedCustomer.location}</div>}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Salesman</Label>
            <Select value={salesmanId} onValueChange={setSalesmanId}>
              <SelectTrigger data-testid="pos-salesman-select"><SelectValue placeholder="Pilih salesman" /></SelectTrigger>
              <SelectContent>{salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Metode Pembayaran</Label>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid grid-cols-2 gap-2" data-testid="pos-payment-method">
              <PaymentOpt value="cash" label="Tunai" icon={Banknote} active={paymentMethod === 'cash'} />
              <PaymentOpt value="transfer" label="Transfer" icon={ArrowLeftRight} active={paymentMethod === 'transfer'} />
              <PaymentOpt value="qris" label="QRIS" icon={QrCode} active={paymentMethod === 'qris'} />
              <PaymentOpt value="tempo" label="Tempo" icon={Clock} active={paymentMethod === 'tempo'} />
            </RadioGroup>
          </div>

          {(paymentMethod === 'transfer' || paymentMethod === 'qris') && (
            <div className="space-y-1.5">
              <Label>Referensi Pembayaran</Label>
              <Input placeholder="No. resi transfer / QRIS" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} data-testid="pos-payment-ref" />
            </div>
          )}

          {paymentMethod === 'tempo' && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3 space-y-3" data-testid="pos-tempo-form">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-700" />
                <div className="text-sm font-medium text-amber-900">Tempo Proyek (mengikuti termin)</div>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Staff Penanggung Jawab <span className="text-rose-600">*</span></Label>
                  <Input value={tempo.staff_pj} onChange={(e) => setTempo({ ...tempo, staff_pj: e.target.value })} placeholder="Nama staff" data-testid="pos-tempo-staff-pj" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nama Proyek / Program <span className="text-rose-600">*</span></Label>
                  <Input value={tempo.project_name} onChange={(e) => setTempo({ ...tempo, project_name: e.target.value })} placeholder="mis. Renovasi Balai Desa" data-testid="pos-tempo-project" />
                </div>
              </div>

              <div className="rounded-md border bg-white p-2 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tempo.has_dp} onChange={(e) => setTempo({ ...tempo, has_dp: e.target.checked })} data-testid="pos-tempo-has-dp" className="h-4 w-4 rounded border-input accent-primary" />
                  <span className="text-sm font-medium">Ada Uang Muka (DP)</span>
                </label>
                {tempo.has_dp && (
                  <div className="space-y-2 pl-6">
                    <RadioGroup value={tempo.dp_type} onValueChange={(v) => setTempo({ ...tempo, dp_type: v })} className="grid grid-cols-2 gap-2" data-testid="pos-tempo-dp-type">
                      <label className={`flex items-center gap-2 rounded border p-2 text-xs cursor-pointer ${tempo.dp_type === 'nominal' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
                        <RadioGroupItem value="nominal" className="sr-only" />Nominal (Rp)
                      </label>
                      <label className={`flex items-center gap-2 rounded border p-2 text-xs cursor-pointer ${tempo.dp_type === 'percentage' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
                        <RadioGroupItem value="percentage" className="sr-only" />Persentase (%)
                      </label>
                    </RadioGroup>
                    <Input
                      type="number" min="0"
                      max={tempo.dp_type === 'percentage' ? 100 : undefined}
                      step={tempo.dp_type === 'percentage' ? '0.1' : '1000'}
                      placeholder={tempo.dp_type === 'percentage' ? 'mis. 30 (%)' : 'Nominal DP (Rp)'}
                      value={tempo.dp_value} onChange={(e) => setTempo({ ...tempo, dp_value: e.target.value })}
                      data-testid="pos-tempo-dp-value"
                    />
                    {total > 0 && (
                      <div className="text-xs text-amber-900 bg-amber-100 rounded p-2 space-y-0.5">
                        <div className="flex justify-between"><span>DP dibayar sekarang</span><span className="tabular-nums font-semibold">{fmtIDR(dpAmount)}</span></div>
                        <div className="flex justify-between"><span>Sisa tempo</span><span className="tabular-nums font-semibold">{fmtIDR(remaining)}</span></div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-md border bg-white p-2 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">Lokasi Program (opsional)</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={tempo.location_kabupaten} onChange={(e) => setTempo({ ...tempo, location_kabupaten: e.target.value })} placeholder="Kabupaten / Kota" data-testid="pos-tempo-loc-kab" />
                  <Input value={tempo.location_desa} onChange={(e) => setTempo({ ...tempo, location_desa: e.target.value })} placeholder="Desa" data-testid="pos-tempo-loc-desa" />
                </div>
                <Input value={tempo.location_pj} onChange={(e) => setTempo({ ...tempo, location_pj: e.target.value })} placeholder="Nama Penanggung Jawab Lokasi" data-testid="pos-tempo-loc-pj" />
              </div>

              <div className="rounded-md border bg-white p-2 space-y-1.5">
                <Label className="text-xs">Tanggal Jatuh Tempo <span className="text-muted-foreground font-normal">(kosongkan jika mengikuti termin)</span></Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="pos-tempo-due-date" />
                <div className="text-[11px] text-muted-foreground">Kosongkan untuk proyek yang jatuh temponya mengikuti termin — status akan tetap "belum bayar" hingga dilunasi.</div>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {cartTotalPpn > 0 && (
          <div className="flex items-center justify-between text-xs">
            <div className="text-muted-foreground">Termasuk PPN 11%</div>
            <div className="tabular-nums font-medium text-violet-700" data-testid="pos-total-ppn">{fmtIDR(cartTotalPpn)}</div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="text-2xl font-heading font-semibold tabular-nums" data-testid="pos-total">{fmtIDR(total)}</div>
        </div>

        {paymentMethod === 'tempo' && total > 0 && (
          <div className="rounded-lg border bg-amber-50 p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">DP dibayar</span><span className="tabular-nums font-semibold">{fmtIDR(dpAmount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Sisa tempo</span><span className="tabular-nums font-semibold text-amber-800">{fmtIDR(remaining)}</span></div>
          </div>
        )}

        <Button onClick={onCheckout} className="w-full h-11" disabled={cart.length === 0 || !customerId || cart.some((c) => !Number(c.quantity) || Number(c.quantity) < 1)} data-testid="pos-checkout-button">
          {!customerId ? 'Pilih Customer Dulu' : (paymentMethod === 'tempo' ? 'Simpan Transaksi Tempo' : 'Bayar & Simpan')}
        </Button>

        {lastTx && (
          <div className="rounded-lg border bg-accent/50 p-3">
            <div className="text-xs text-accent-foreground">Transaksi terakhir: <b>{lastTx.invoice_no}</b></div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => onDownloadPDF(lastTx)} data-testid="pos-print-pdf-button"><Download className="h-3.5 w-3.5 mr-1.5" />PDF</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => onPrintBT(lastTx)} data-testid="pos-print-bluetooth-button"><Printer className="h-3.5 w-3.5 mr-1.5" />Bluetooth</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentOpt({ value, label, icon: Icon, active }) {
  return (
    <label className={`flex flex-col items-center justify-center rounded-lg border p-2 cursor-pointer text-xs transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
      <RadioGroupItem value={value} className="sr-only" />
      <Icon className="h-4 w-4 mb-1" />
      {label}
    </label>
  );
}


function CustomerCombobox({ customers, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const selected = customers.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal h-10',
            !value && 'border-rose-300 text-muted-foreground'
          )}
          data-testid="pos-customer-combobox"
        >
          <span className="truncate text-left">
            {selected ? (
              <span>
                <span className="font-medium">{selected.name}</span>
                {selected.company && <span className="text-muted-foreground text-xs"> • {selected.company}</span>}
              </span>
            ) : (
              'Ketik nama customer...'
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue is our custom searchable string; case-insensitive match
            const v = (itemValue || '').toLowerCase();
            const s = (search || '').toLowerCase().trim();
            if (!s) return 1;
            return v.includes(s) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cari nama, perusahaan, WA, lokasi..." data-testid="pos-customer-search" />
          <CommandList>
            <CommandEmpty>
              <div className="py-6 text-center text-sm">
                <div className="text-muted-foreground mb-2">Customer tidak ditemukan</div>
                <div className="text-xs text-muted-foreground">Klik tombol "Baru" untuk mendaftarkan.</div>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {customers.map((c) => {
                const searchable = [c.name, c.company, c.whatsapp, c.location].filter(Boolean).join(' ');
                return (
                  <CommandItem
                    key={c.id}
                    value={searchable}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                    data-testid={`pos-customer-option-${c.id}`}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {(c.company || c.whatsapp || c.location) && (
                        <div className="text-[10px] text-muted-foreground truncate">
                          {[c.company, c.whatsapp, c.location].filter(Boolean).join(' • ')}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
